import React, { useCallback, useEffect, useState } from "react";
import { attendanceAPI, profileAPI, withFileToken } from "../services/api";
import useAttendance from "../hooks/useAttendance";
import CameraCapture from "../components/common/CameraCapture";
import Toast from "../components/common/Toast";
import { PageLoader } from "../components/common/LoadingSpinner";
import { reverseGeocode, getDistanceMeters, isGeofenceActive } from "../utils/helpers";

const DEFAULT_WORK_MODES = [
  { value: "onsite", label: "Normal" },
  { value: "sales", label: "Sales" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "sopir", label: "Sopir" },
  { value: "wfh", label: "WFH" },
];

const SALES_WORK_MODES = [
  { value: "sales", label: "Sales" },
  { value: "wfh", label: "WFH" },
];

const ECOMMERCE_WORK_MODES = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "wfh", label: "WFH" },
];

const REGULAR_MOBILE_WORK_MODES = [
  { value: "onsite", label: "Normal" },
  { value: "wfh", label: "WFH" },
];

const DRIVER_WORK_MODES = [
  { value: "sopir", label: "Sopir" },
];

const workModeLabel = (value, modes = DEFAULT_WORK_MODES) => {
  return (
    modes.find((item) => item.value === value)?.label ||
    DEFAULT_WORK_MODES.find((item) => item.value === value)?.label ||
    "Absensi"
  );
};

const isSalesAttendanceType = (attendanceType) => {
  const value = String(attendanceType?.code || attendanceType?.name || "").toLowerCase();
  return value.includes("sales") || value.includes("field") || value.includes("lapangan");
};

const isEcommerceAttendanceType = (attendanceType) => {
  const value = String(attendanceType?.code || attendanceType?.name || "").toLowerCase();
  return value.includes("ecommerce") || value.includes("e-commerce") || value.includes("e_commerce");
};

const isDriverAttendanceType = (attendanceType) => {
  const value = String(attendanceType?.code || attendanceType?.name || "").toLowerCase();
  return value.includes("sopir") || value.includes("supir") || value.includes("driver");
};

const normalizeWorkMode = (value) => {
  const mode = String(value || "").toLowerCase();
  if (["sales", "sale", "field", "lapangan"].includes(mode)) return "sales";
  if (["ecommerce", "e-commerce", "e_commerce", "online", "marketplace"].includes(mode)) return "ecommerce";
  if (["sopir", "supir", "driver"].includes(mode)) return "sopir";
  if (["wfh", "home", "remote"].includes(mode)) return "wfh";
  return "onsite";
};

const filterWorkModes = (modes, allowedValues) => {
  const source = Array.isArray(modes) ? modes : [];
  return source.filter((item) => allowedValues.includes(item?.value));
};

const hasTimezone = (value) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(String(value || ""));
const pad2 = (value) => String(value).padStart(2, "0");

function toWIBTime(value) {
  if (!value) return "--:--";
  const raw = String(value).trim();

  if (!hasTimezone(raw)) {
    const timePart = raw.includes("T")
      ? raw.split("T")[1]
      : raw.includes(" ")
        ? raw.split(" ")[1]
        : raw;
    const [hour = "00", minute = "00"] = timePart.split(":");
    return `${pad2(hour)}:${pad2(minute)}`;
  }

  return new Date(raw).toLocaleTimeString("id-ID", {
    timeZone: "Asia/Jakarta",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

function toWIBDate(value) {
  if (!value) return "";
  const raw = String(value).trim();

  if (!hasTimezone(raw)) {
    const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
    const [year, month, day] = datePart.split("-").map(Number);
    if (!year || !month || !day) return "";
    return new Date(year, month - 1, day).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  return new Date(raw).toLocaleDateString("id-ID", {
    timeZone: "Asia/Jakarta",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatWorked(h) {
  if (!h && h !== 0) return null;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm === 0 ? `${hh} jam` : `${hh} jam ${mm} mnt`;
}

async function getGPS() {
  return new Promise((res, rej) =>
    navigator.geolocation
      ? navigator.geolocation.getCurrentPosition(res, rej, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0,
        })
      : rej(new Error("Geolocation tidak didukung"))
  );
}

function SelfieCard({ label, isoTime, address, imageUrl, accent }) {
  const hasData = !!isoTime;
  const isGreen = accent === "green";
  const tone = isGreen ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-primary";
  const src = imageUrl ? withFileToken(imageUrl) : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* Foto hanya ditampilkan bila memang ada */}
      {src && (
        <div className="h-28 w-full bg-gray-100">
          <img key={src} src={src} alt={`selfie ${label}`} className="h-full w-full object-cover" />
        </div>
      )}

      <div className="flex items-center gap-2.5 p-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tone}`}>
          <CameraIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
          <p className="mt-0.5 text-lg font-black leading-none text-gray-900">{hasData ? toWIBTime(isoTime) : "--:--"}</p>
          <p className="mt-0.5 text-[10px] font-semibold text-gray-400">{hasData ? toWIBDate(isoTime) : "Belum tercatat"}</p>
        </div>
      </div>

      {hasData && (address || !src) && (
        <div className="px-3 pb-3">
          <p className="line-clamp-2 rounded-lg bg-gray-50 px-2.5 py-1.5 text-[10px] font-medium leading-snug text-gray-500">
            {address || "Lokasi tidak tercatat"}
          </p>
        </div>
      )}
    </div>
  );
}

export default function AttendancePage() {
  const [today, setToday] = useState(null);
  const [loadingData, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [mode, setMode] = useState(null);
  const [workMode, setWorkMode] = useState("onsite");
  const [availableWorkModes, setAvailableWorkModes] = useState(DEFAULT_WORK_MODES);
  const [attendanceType, setAttendanceType] = useState({ code: "regular", name: "Regular" });
  const [gpsStatus, setGpsStatus] = useState("idle");
  const [gpsCoords, setGpsCoords] = useState(null);
  const [workLocation, setWorkLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [translatedAddresses, setTranslatedAddresses] = useState({ checkin: null, checkout: null });
  const { loading, error, success, info, doCheckIn, doCheckOut, setError, setSuccess, setInfo } = useAttendance();

  const fetchToday = useCallback(async () => {
    try {
      const data = await attendanceAPI.today();
      setToday(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!today) return;
      const newAddresses = {};

      if (
        today.checkin_lat &&
        today.checkin_lon &&
        !today.checkin_address &&
        !translatedAddresses.checkin
      ) {
        const addr = await reverseGeocode(today.checkin_lat, today.checkin_lon);
        if (addr) newAddresses.checkin = addr;
      }

      if (
        today.checkout_lat &&
        today.checkout_lon &&
        !today.checkout_address &&
        !translatedAddresses.checkout
      ) {
        const addr = await reverseGeocode(today.checkout_lat, today.checkout_lon);
        if (addr) newAddresses.checkout = addr;
      }

      if (!cancelled && (newAddresses.checkin || newAddresses.checkout)) {
        setTranslatedAddresses((prev) => ({ ...prev, ...newAddresses }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [today, translatedAddresses.checkin, translatedAddresses.checkout]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  useEffect(() => {
    const onRefresh = () => fetchToday();
    window.addEventListener("hris:app-refresh", onRefresh);
    return () => window.removeEventListener("hris:app-refresh", onRefresh);
  }, [fetchToday]);

  useEffect(() => {
    (async () => {
      try {
        const profile = await profileAPI.get();
        setWorkLocation(profile?.work_location || null);
        const profileAttendanceType = profile?.attendance_employee_type || { code: "regular", name: "Regular" };
        const defaultWorkMode = normalizeWorkMode(profile?.default_work_mode);
        const isSales = isSalesAttendanceType(profileAttendanceType);
        const isEcommerce = isEcommerceAttendanceType(profileAttendanceType);
        const isDriver =
          isDriverAttendanceType(profileAttendanceType) ||
          /sopir|supir|driver/i.test(String(profile?.job_title || ""));
        const profileModes = profile?.available_work_modes || [];
        const modeType = defaultWorkMode === "sales" || defaultWorkMode === "ecommerce" || defaultWorkMode === "sopir"
          ? defaultWorkMode
          : isSales
            ? "sales"
            : isEcommerce
              ? "ecommerce"
              : isDriver
                ? "sopir"
                : "onsite";
        const modes = modeType === "sopir"
          ? DRIVER_WORK_MODES
          : modeType === "sales"
            ? filterWorkModes(profileModes, ["sales", "wfh"])
            : modeType === "ecommerce"
              ? filterWorkModes(profileModes, ["ecommerce", "wfh"])
              : filterWorkModes(profileModes, ["wfh"]);
        const fallbackModes = modeType === "sales"
          ? SALES_WORK_MODES
          : modeType === "ecommerce"
            ? ECOMMERCE_WORK_MODES
            : modeType === "sopir"
              ? DRIVER_WORK_MODES
              : REGULAR_MOBILE_WORK_MODES;
        const finalModes = modes.length ? modes : fallbackModes;
        const defaultMode = normalizeWorkMode(today?.work_mode || defaultWorkMode);

        setAttendanceType(profileAttendanceType);
        setAvailableWorkModes(finalModes);
        setWorkMode(finalModes.some((item) => item.value === defaultMode) ? defaultMode : finalModes[0].value);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [today?.work_mode]);

  useEffect(() => {
    const todayWorkMode = normalizeWorkMode(today?.work_mode);
    if (today?.work_mode && availableWorkModes.some((item) => item.value === todayWorkMode)) {
      setWorkMode(todayWorkMode);
    }
  }, [today?.work_mode, availableWorkModes]);

  useEffect(() => {
    (async () => {
      setGpsStatus("detecting");
      try {
        const pos = await getGPS();
        setGpsCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setGpsStatus("ok");
      } catch {
        setGpsStatus("error");
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const isCheckedIn = !!today?.check_in && !today?.check_out;
  const isCheckedOut = !!today?.check_out;
  const isSalesEmployee = isSalesAttendanceType(attendanceType);
  const isEcommerceEmployee = isEcommerceAttendanceType(attendanceType);
  const isDriverEmployee = isDriverAttendanceType(attendanceType);
  const isSalesMobileMode = availableWorkModes.some((item) => item.value === "sales") || isSalesEmployee;
  const isEcommerceMobileMode = availableWorkModes.some((item) => item.value === "ecommerce") || isEcommerceEmployee;
  const isDriverMobileMode = availableWorkModes.some((item) => item.value === "sopir") || isDriverEmployee;
  const canChooseWorkMode = !isDriverMobileMode && availableWorkModes.length > 1;
  const checkInActionLabel = workMode === "wfh"
    ? "Check In WFH"
    : workMode === "sales"
      ? "Check In Sales"
      : workMode === "ecommerce"
        ? "Check In E-commerce"
      : workMode === "sopir"
        ? "Check In Sopir"
      : "Check In Sekarang";
  const attendanceNote = isSalesMobileMode
    ? {
        label: "Catatan Sales",
        text: "Jika hari ini WFH, pilih WFH sebelum check in agar tercatat sebagai WFH Sales.",
        wrapperClass: "bg-sky-50 border-sky-100",
        labelClass: "text-sky-600",
        textClass: "text-sky-700",
      }
    : isEcommerceMobileMode
      ? {
          label: "Catatan E-commerce",
          text: "Absensi E-commerce default tercatat hadir. Pilih WFH hanya jika bekerja dari rumah.",
          wrapperClass: "bg-indigo-50 border-indigo-100",
          labelClass: "text-indigo-500",
          textClass: "text-indigo-700",
        }
    : isDriverMobileMode
      ? {
          label: "Catatan Sopir",
          text: "Absensi sopir dilakukan dari mobile saat bekerja di lapangan dan tercatat sebagai absensi normal.",
          wrapperClass: "bg-emerald-50 border-emerald-100",
          labelClass: "text-emerald-600",
          textClass: "text-emerald-800",
        }
      : {
          label: "Catatan Absensi",
          text: "Pilih Normal untuk absensi kerja seperti biasa, atau pilih WFH jika hari ini bekerja dari rumah.",
          wrapperClass: "bg-amber-50 border-amber-100",
          labelClass: "text-amber-600",
          textClass: "text-amber-800",
        };
  const lockedWorkModeTitle = isDriverMobileMode ? "Sopir Lapangan" : workModeLabel(workMode, availableWorkModes);
  const lockedWorkModeDescription = isDriverMobileMode
    ? "Mode otomatis untuk absensi mobile sopir"
    : "Mode otomatis untuk absensi mobile";
  const lockedWorkModeClass = isDriverMobileMode
    ? "border-emerald-100 bg-emerald-50"
    : "border-red-100 bg-red-50";
  const lockedWorkModeBadgeClass = isDriverMobileMode
    ? "border-emerald-100 text-emerald-700 bg-white"
    : "border-red-100 text-primary bg-white";
  const statusLabel = loading
    ? "Memproses"
    : gpsStatus !== "ok"
      ? "Menunggu GPS"
      : isCheckedOut
        ? "Absensi Selesai"
        : isCheckedIn
          ? "Sudah Check In"
          : "Belum Check In";
  const statusCopy = loading
    ? "Mohon tunggu sebentar"
    : gpsStatus === "detecting"
      ? "GPS sedang mendeteksi lokasi kamu"
      : gpsStatus === "error"
        ? "Aktifkan izin lokasi di browser"
        : isCheckedOut
          ? "Sampai jumpa besok."
          : isCheckedIn
            ? "Siap untuk check out kapanpun."
            : "Tap tombol di bawah untuk mulai absensi.";
  const statusTone = isCheckedOut
    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
    : isCheckedIn
      ? "bg-amber-50 text-amber-600 border-amber-100"
      : "bg-red-50 text-primary border-red-100";
  const gpsText = gpsStatus === "ok" ? "GPS Terhubung"
    : gpsStatus === "detecting" ? "Mendeteksi GPS"
    : gpsStatus === "error" ? "GPS Tidak Aktif"
    : "Menunggu GPS";

  // Geofence: bebas absen di mana saja secara default. Kalau admin sudah
  // set lat/lng/radius work_location di Odoo, absen wajib dalam radius itu
  // (server tetap otoritas final validasi ini — lihat backend attendance.service.js).
  // Radius hanya untuk mode onsite (Normal); WFH/sales/ecommerce/sopir bebas lokasi.
  // Saat check-out, workMode sudah ikut mode check-in (today.work_mode).
  const geofenceActive = workMode === "onsite" && isGeofenceActive(workLocation);
  const distanceToWork = geofenceActive && gpsCoords
    ? getDistanceMeters(gpsCoords.lat, gpsCoords.lon, Number(workLocation.latitude), Number(workLocation.longitude))
    : null;
  const outsideGeofence = geofenceActive && distanceToWork != null && distanceToWork > Number(workLocation.radius);

  const handleAction = () => {
    if (gpsStatus !== "ok") {
      setError("GPS belum aktif. Aktifkan izin lokasi terlebih dahulu.");
      return;
    }
    if (!isCheckedIn && !workMode) {
      setError("Pilih work mode terlebih dahulu.");
      return;
    }
    if (outsideGeofence) {
      setError("Anda di luar jangkauan radius lokasi kerja.");
      return;
    }
    setMode(isCheckedIn ? "out" : "in");
    setShowCamera(true);
  };

  const handleCapture = async (base64) => {
    setShowCamera(false);
    const payload = { latitude: gpsCoords.lat, longitude: gpsCoords.lon, image: base64, workMode };
    const result = mode === "in"
      ? await doCheckIn(payload)
      : await doCheckOut(payload);
    if (result) {
      setToday((prev) => ({
        ...(prev || {}),
        ...(mode === "in"
          ? {
              check_in: result.check_in,
              checkin_address: result.checkin_address,
              checkin_image_url: result.checkin_image_url,
              work_mode: result.work_mode,
              is_wfh: result.is_wfh,
              attendance_employee_type: result.attendance_employee_type,
            }
          : { check_out: result.check_out, worked_hours: result.worked_hours, checkout_address: result.checkout_address, checkout_image_url: result.checkout_image_url }),
        pending_sync: !!result.queued,
      }));
    }
    fetchToday();
  };

  if (loadingData) return <PageLoader />;

  return (
    <div className="flex min-h-[calc(100dvh-80px)] flex-col bg-gray-50 text-gray-900 antialiased">
      {showCamera && <CameraCapture onCapture={handleCapture} onClose={() => setShowCamera(false)} />}
      {(success || error || info) && (
        <Toast
          message={success || error || info}
          type={success ? "success" : error ? "error" : "info"}
          onClose={() => { setSuccess(null); setError(null); setInfo(null); }}
        />
      )}

      <header className="px-4 pt-4">
        <div className="rounded-2xl bg-primary p-4 text-white shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Waktu Sekarang · WIB</p>
              <p className="mt-1 text-3xl font-black leading-none tracking-tight tabular-nums">
                {currentTime.toLocaleTimeString("id-ID", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  timeZone: "Asia/Jakarta",
                  hourCycle: "h23",
                })}
              </p>
              <p className="mt-1.5 text-[11px] font-semibold text-white/80">
                {currentTime.toLocaleDateString("id-ID", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  timeZone: "Asia/Jakarta",
                })}
              </p>
            </div>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-lg font-black">
              {currentTime.getDate()}
            </span>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5">
            <span className={`h-2 w-2 rounded-full ${gpsStatus === "ok" ? "bg-emerald-300" : gpsStatus === "error" ? "bg-red-300" : "bg-amber-300 animate-pulse"}`} />
            <span className="text-[11px] font-bold">{gpsText}</span>
          </div>

          {geofenceActive && distanceToWork != null && (
            <p className={`mt-1.5 text-[10px] font-bold ${outsideGeofence ? "text-red-200" : "text-white/70"}`}>
              {outsideGeofence ? "⚠ Di luar radius: " : "✓ "}
              {Math.round(distanceToWork)} m dari {workLocation.name || "lokasi kerja"} (radius {Math.round(Number(workLocation.radius))} m)
            </p>
          )}
        </div>
      </header>

      <main className="flex flex-1 flex-col px-4 pb-6">
        <section className="mt-3 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${statusTone}`}>
                  {isCheckedOut ? "Selesai" : isCheckedIn ? "Berlangsung" : "Belum Absen"}
                </span>
                {today?.pending_sync && (
                  <span className="ml-1.5 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                    Menunggu dikirim
                  </span>
                )}
                <h1 className="mt-2 text-lg font-black leading-tight text-gray-900">{statusLabel}</h1>
                <p className="mt-1 text-[11px] font-medium leading-snug text-gray-500">{statusCopy}</p>
              </div>
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${statusTone}`}>
                <ClockIcon />
              </span>
            </div>

            {!isCheckedOut && (
              <div className={`relative mt-3 rounded-xl border px-3 py-2.5 ${attendanceNote.wrapperClass}`}>
                <p className={`text-[10px] font-black uppercase tracking-wider ${attendanceNote.labelClass}`}>{attendanceNote.label}</p>
                <p className={`mt-1 text-[11px] font-bold leading-snug ${attendanceNote.textClass}`}>{attendanceNote.text}</p>
              </div>
            )}

            {isCheckedOut && formatWorked(today?.worked_hours) && (
              <div className="relative mt-3 inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2">
                <ClockIcon />
                <span className="text-xs font-black text-emerald-700">{formatWorked(today.worked_hours)}</span>
                <span className="text-[10px] font-bold text-emerald-600">total kerja</span>
              </div>
            )}

            {!isCheckedOut && (
              <div className="relative mt-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Jenis Absensi</p>
                  {isCheckedIn && (
                    <span className="rounded-lg border border-amber-100 bg-amber-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-amber-700">
                      {workModeLabel(workMode, availableWorkModes)}
                    </span>
                  )}
                </div>

                {canChooseWorkMode ? (
                  <div className="grid grid-cols-2 gap-2">
                    {availableWorkModes.map((item) => {
                      const active = workMode === item.value;
                      return (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => !isCheckedIn && setWorkMode(item.value)}
                          disabled={isCheckedIn || loading}
                          className={`h-11 rounded-xl border text-xs font-bold transition ${
                            active
                              ? "border-primary bg-primary text-white shadow-sm"
                              : "border-gray-200 bg-white text-gray-600"
                          } disabled:opacity-70`}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`rounded-xl border px-3 py-2.5 flex items-center justify-between gap-3 ${lockedWorkModeClass}`}>
                    <div>
                      <p className="text-[12px] font-black text-gray-900">{lockedWorkModeTitle}</p>
                      <p className="mt-0.5 text-[10px] font-bold text-gray-500">{lockedWorkModeDescription}</p>
                    </div>
                    <span className={`shrink-0 rounded-lg border px-2.5 py-1 text-[9px] font-black uppercase tracking-wider ${lockedWorkModeBadgeClass}`}>Terkunci</span>
                  </div>
                )}
              </div>
            )}

            {!isCheckedOut && (
              <button
                onClick={handleAction}
                disabled={loading || gpsStatus !== "ok"}
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-black text-white shadow-sm transition active:scale-[0.99] disabled:opacity-50 ${isCheckedIn ? "bg-orange-600" : "bg-primary"}`}
              >
                {!loading && gpsStatus === "ok" && <CameraIcon />}
                {loading ? "Memproses..." : gpsStatus !== "ok" ? "Menunggu GPS..." : isCheckedIn ? "Check Out Sekarang" : checkInActionLabel}
              </button>
            )}

            {gpsStatus === "error" && (
              <div className="relative mt-3 flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5">
                <AlertIcon />
                <div>
                  <p className="text-xs font-black text-red-700">GPS Tidak Aktif</p>
                  <p className="mt-0.5 text-[11px] font-semibold text-red-500">Aktifkan izin lokasi di pengaturan browser kamu.</p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-3">
          <div className="mb-2 flex items-center justify-between px-0.5">
            <h2 className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Foto Absensi</h2>
            <span className="text-[11px] font-bold uppercase tracking-wide text-primary">Hari Ini</span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <SelfieCard
              label="Check In"
              isoTime={today?.check_in}
              address={today?.checkin_address || translatedAddresses.checkin}
              imageUrl={today?.checkin_image_url}
              accent="green"
            />
            <SelfieCard
              label="Check Out"
              isoTime={today?.check_out}
              address={today?.checkout_address || translatedAddresses.checkout}
              imageUrl={today?.checkout_image_url}
              accent="red"
            />
          </div>
        </section>

        <section className="mt-3 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-sm">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-primary">
            <LockIcon />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-black text-gray-800">Foto &amp; GPS dicatat untuk verifikasi</p>
            <p className="mt-0.5 text-[11px] font-medium text-gray-400">Data absensi aman &amp; hanya untuk keperluan HR.</p>
          </div>
        </section>
      </main>
    </div>
  );
}

function ClockIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-red-600">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}
